import { Close, GetApp } from "@mui/icons-material";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Slide,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

import { isIOS, isStandalone } from "../../lib/pwa-utils";

const INSTALL_PROMPT_DISMISSED_KEY = "pwa-install-prompt-dismissed";

/**
 * InstallPrompt - Shows an install banner for PWA installation
 *
 * On Android: Uses the beforeinstallprompt event to show native install dialog
 * On iOS: Shows instructions to use "Add to Home Screen" in Safari
 *
 * The prompt is dismissed for 7 days after user closes it.
 */
export default function InstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);

  useEffect(() => {
    // Don't show if already installed
    if (isStandalone()) {
      return;
    }

    // Check if user dismissed recently
    const dismissedAt = localStorage.getItem(INSTALL_PROMPT_DISMISSED_KEY);
    if (dismissedAt) {
      const dismissedTime = parseInt(dismissedAt, 10);
      const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedTime < sevenDaysMs) {
        return;
      }
    }

    // iOS detection - show custom instructions
    if (isIOS()) {
      // Wait a bit before showing iOS instructions
      const timer = setTimeout(() => {
        setShowIOSInstructions(true);
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // Android/Chrome - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Listen for successful installation
    const handleAppInstalled = () => {
      setShowPrompt(false);
      setDeferredPrompt(null);
      console.log("PWA was installed");
    };

    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      return;
    }

    // Show the native install prompt
    deferredPrompt.prompt();

    // Wait for user response
    const { outcome } = await deferredPrompt.userChoice;
    console.log(`User response to install prompt: ${outcome}`);

    // Clear the deferred prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    localStorage.setItem(INSTALL_PROMPT_DISMISSED_KEY, Date.now().toString());
  }, []);

  if (!showPrompt) {
    return null;
  }

  return (
    <Slide direction="up" in={showPrompt} mountOnEnter unmountOnExit>
      <Paper
        elevation={6}
        style={{
          position: "fixed",
          bottom: 16,
          left: 16,
          right: 16,
          zIndex: 1300,
          borderRadius: 8,
          overflow: "hidden",
        }}
      >
        <Box
          style={{
            padding: "16px",
            display: "flex",
            alignItems: "flex-start",
          }}
        >
          <GetApp
            style={{ marginRight: "12px", marginTop: "2px", color: "#1976d2" }}
          />
          <Box style={{ flex: 1 }}>
            <Typography
              style={{ fontWeight: 600, marginBottom: "4px" }}
              variant="subtitle1"
            >
              Nainstalovat aplikaci
            </Typography>
            {showIOSInstructions ? (
              <Typography color="textSecondary" variant="body2">
                Klepněte na tlačítko Sdílet a poté na &bdquo;Přidat na
                plochu&ldquo;
              </Typography>
            ) : (
              <Typography color="textSecondary" variant="body2">
                Přidejte OM Platform na svou plochu pro rychlejší přístup
              </Typography>
            )}
          </Box>
          <IconButton
            aria-label="Zavřít"
            onClick={handleDismiss}
            size="small"
            style={{ marginLeft: "8px" }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
        {!showIOSInstructions && (
          <Box
            style={{
              padding: "0 16px 16px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Button
              color="primary"
              disableElevation
              onClick={handleInstallClick}
              variant="contained"
            >
              Instalovat
            </Button>
          </Box>
        )}
      </Paper>
    </Slide>
  );
}
